import * as THREE from 'three'

/**
 * Common Maths Rectangle class for a 2D rectangle
 * Contains helper functions for handling rectangles
 */
export default class Rectangle {
  constructor(x = 0, y = 0, width = 0, height = 0) {
    this.x = x
    this.y = y
    this.width = width
    this.height = height
  }


  /**
   * Clone this rectangle
   */
  clone() {
    return new Rectangle(this.x, this.y, this.width, this.height);
  }


  /**
   * Sets the rectangle using the absolute bounds
   */
  setBounds(left, top, right, bottom) {
    this.x = left;
    this.y = top;
    this.width = right - left;
    this.height = bottom - top;
  }


  /**
   * Set from a THREE.JS Box2 object
   */
  setFromBox2(box) {
    this.setBounds(box.min.x, box.min.y, box.max.x, box.max.y)
  }


  /**
   * Checks if the rectangle is empty
   */
  isEmpty() {
    return this.getLeft() >= this.getRight() || this.getTop() >= this.getBottom();
  }


  /**
   * Get the left coordinate of the rectangle
   */
  getLeft() {
    return this.x;
  }


  /**
   * Get the top coordinate of the rectangle
   */
  getTop() {
    return this.y;
  }


  /**
   * Get the right coordinate of the rectangle
   */
  getRight() {
    return this.x + this.width;
  }


  /**
   * Get the bottom coordinate of the rectangle
   */
  getBottom() {
    return this.y + this.height;
  }


  /**
   * Get the width of the rectangle
   */
  getWidth() {
    return this.width;
  }


  /**
   * Get the height of the rectangle
   */
  getHeight() {
    return this.height;
  }


  /**
   * Calculate the area of the rectangle
   */
  getArea() {
    return this.width * this.height;
  }


  /**
   * Grow/Shrink the rectangle in all directions by a distance
   */
  growRectangle(amount) {
    this.setBounds(this.getLeft() - amount, this.getTop() - amount, this.getRight() + amount, this.getBottom() + amount)
  }


  /**
   * Check if the rectangle contains a point
   */
  isPointInside(x, y) {
    return (x < this.x || x > this.getRight() || y < this.y || y > this.getBottom()) ? false : true;
  }


  /**
   * Checks if the rectangle fully contains other rectangle
   */
  containsRect(rect) {
		return this.x <= rect.x && rect.getRight() <= this.getRight() && this.y <= rect.y && rect.getBottom() <= this.getBottom();
	}


  /**
   * Get the center point of the rectangle
   * Returns a THREE.Vector2() object
   */
  getCenter() {
    return new THREE.Vector2(this.x + this.width/2, this.y + this.height/2)
  }


  /**
   * Checks if the rectangle intersects with another rectangle
   */
  intersectsBox(rect) {
		return (rect.getRight() < this.x || rect.x > this.getRight() || rect.getBottom() < this.y || rect.y > this.getBottom()) ? false : true;
	}


  /**
   * Calculate the rectangle region this rectangle and another intersects
   */
  intersect(rect) {
    if (this.isEmpty() || rect.isEmpty())
      return new Rectangle(0, 0, 0, 0);

    let x1 = Math.max(this.getLeft(), rect.getLeft());
    let x2 = Math.min(this.getRight(), rect.getRight());
    let y1 = Math.max(this.getTop(), rect.getTop());
    let y2 = Math.min(this.getBottom(), rect.getBottom());

    return new Rectangle(x1, y1, Math.max(0, x2 - x1), Math.max(0, y2 - y1));
  }


  /**
   * Runs a subtraction operation on this rectangle using another rectangle
   * Returns a list of resulting rectangles minus the intersecting region
   */
  subtract(rect) {
    let resultRects = [];
    let tmpRect = new Rectangle(0, 0, 0, 0);

    rect = rect.intersect(this);
    if(rect.isEmpty())
      return [this.clone()];

    // left strip
    tmpRect.setBounds(this.getLeft(), this.getTop(), rect.getLeft(), this.getBottom());
    if (!tmpRect.isEmpty())
      resultRects.push(tmpRect.clone());
    
    // inside strip
    tmpRect.setBounds(rect.getLeft(), this.getTop(), rect.getRight(), rect.getTop());
    if (!tmpRect.isEmpty())
      resultRects.push(tmpRect.clone());

    tmpRect.setBounds(rect.getLeft(), rect.getBottom(), rect.getRight(), this.getBottom());
    if (!tmpRect.isEmpty())
      resultRects.push(tmpRect.clone());

    // right strip
    tmpRect.setBounds(rect.getRight(), this.getTop(), this.getRight(), this.getBottom());
    if (!tmpRect.isEmpty())
      resultRects.push(tmpRect.clone());

    return resultRects;
  }


  /*
   * Expand the rectangle given a point
   */
  expandByPoint(x, y) {
    if(this.isEmpty())
      this.setBounds(x, y, x+1, y+1);
    else {
      let left = Math.min(this.getLeft(), x);
      let top = Math.min(this.getTop(), y);
      let right = Math.max(this.getRight(), x);
      let bottom = Math.max(this.getBottom(), y);

      this.setBounds(left, top, right, bottom);
    }
  }


  /**
   * Expand the rectangle given another rectangle
   */
  expandByRectangle(rect) {
    this.expandByPoint(rect.getLeft(), rect.getTop())
    this.expandByPoint(rect.getRight(), rect.getBottom())
  }


  /**
   * Check if an axis-aligned line intersects with the rectangle
   */
  calculateAxisLineIntersection(startX, startY, endX, endY) {
    let lineRect = new Rectangle(startX, startY, 1, 1)
    lineRect.expandByPoint(endX, endY)

    return this.intersectsBox(lineRect)
  }



  /**
   * Convert the rectangle to a displayable string
   */
  toString() {
    return `[pos: ${this.x}x${this.y} size:${this.width}x${this.height}]`
  }
}